import { DeleteOutlined } from '@ant-design/icons';
import {
  type RelationEditor,
  type WithId,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { DecimalInput } from '@framework/form/inputs/DecimalInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { Button, Flex, Space, Table, Tooltip } from 'antd';
import { type ColumnType } from 'antd/es/table';
import Decimal from 'decimal.js-light';
import type OLMap from 'ol/Map';
import type VectorSource from 'ol/source/Vector';
import { type ReactNode, useMemo } from 'react';
import { type MapIntersectionBaseItem } from '../createMapSchema.ts';
import { MapUnitRender } from '../layers/rendering/MapUnitRender.tsx';
import { MapFlashFeatureButton } from './MapFlashFeatureButton.tsx';

/** Props per la tabella delle intersezioni (catastali e custom). */
export interface IMapIntersectionsTableProps {
  /** Colonne centrali (tra "Geom N" e "Area interessata"), pre-costruite dal parent */
  columns: ColumnType<MapIntersectionBaseItem>[];
  /** Editor (catastale o custom) */
  editor: RelationEditor<MapIntersectionBaseItem>;
  /** Mappa record → indice assoluto, condivisa col padre (evita ricalcolo) */
  indexOfItem: Map<WithId<MapIntersectionBaseItem>, number>;
  /** Nome campo form per i path RHF */
  fieldName: string;
  /** Item di default creato dal pulsante "Aggiungi" */
  defaultItem: MapIntersectionBaseItem;
  mapRef: React.RefObject<OLMap | null>;
  featuresSource: React.RefObject<VectorSource>;
  readonly?: boolean;
  geometryLabelPrefix?: string;
}

/**
 * Tabella per le intersezioni di un layer.
 * Riceve colonne pre-costruite e gestisce solo le colonne fisse
 * "Geom N" e "Area interessata".
 */
export function MapIntersectionsTable(props: IMapIntersectionsTableProps) {
  const {
    columns,
    editor,
    indexOfItem,
    fieldName,
    defaultItem,
    mapRef,
    featuresSource,
    readonly,
    geometryLabelPrefix,
  } = props;
  const prefix = geometryLabelPrefix ?? 'Geom. #';
  const { visuallyDisabled } = useFormItemEnabled();
  const totalColumns = columns.length + 2;

  const totalAffectedAreaSummary = useMemo<
    (() => ReactNode) | undefined
  >(() => {
    if (editor.items.length === 0) return undefined;
    const totalAffectedArea = editor.items
      .reduce((sum, item) => sum.plus(item?.affectedArea ?? 0), new Decimal(0))
      .toNumber();
    return () => (
      <Table.Summary.Row>
        <Table.Summary.Cell index={totalColumns} colSpan={totalColumns}>
          <Flex align="center" justify="flex-end">
            <span>Totale aree interessate:</span>
            <MapUnitRender value={totalAffectedArea} />
          </Flex>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  }, [editor.items, totalColumns]);

  const appendItemFooter = useMemo<(() => ReactNode) | undefined>(
    () =>
      readonly
        ? undefined
        : () => (
            <Button
              disabled={visuallyDisabled}
              onClick={() => editor.append(defaultItem)}
            >
              Aggiungi
            </Button>
          ),
    [readonly, visuallyDisabled, editor, defaultItem],
  );

  return (
    <div style={{ overflowY: 'scroll' }}>
      <TableData<WithId<MapIntersectionBaseItem>>
        dataSource={editor.items}
        rowKey={(record, i) => record.__uid ?? record.feature ?? String(i)}
        footer={appendItemFooter}
        summary={totalAffectedAreaSummary}
        columns={[
          {
            title: prefix,
            dataIndex: 'sourceGeometryIndex',
            key: 'sourceGeometryIndex',
            width: '80px',
            render: (value: number | undefined) => value ?? '—',
          },
          ...columns,
          {
            title: 'Area interessata',
            dataIndex: 'affectedArea',
            key: 'affectedArea',
            width: '200px',
            render: (
              _value: number,
              record: WithId<MapIntersectionBaseItem>,
            ) => {
              const rowIndex = indexOfItem.get(record) ?? -1;
              return (
                <Space>
                  <FormItem
                    name={`${fieldName}.${rowIndex}.affectedArea`}
                    label="Area interessata"
                    readonly={readonly}
                    layout={{
                      removeHelpMargin: true,
                      removeLabelSpace: true,
                      hideVisualLabel: true,
                    }}
                  >
                    <DecimalInput
                      name={`${fieldName}.${rowIndex}.affectedArea`}
                      readOnly={readonly}
                      suffix="mq"
                    />
                  </FormItem>
                  <MapFlashFeatureButton
                    mapRef={mapRef}
                    featuresSource={featuresSource}
                    geometryIndex={record.sourceGeometryIndex}
                  />
                  {record.isManual && !readonly && (
                    <Tooltip title="Rimuovi area">
                      <Button
                        icon={<DeleteOutlined />}
                        disabled={visuallyDisabled}
                        danger
                        onClick={() => {
                          editor.removeAt(rowIndex);
                        }}
                      />
                    </Tooltip>
                  )}
                </Space>
              );
            },
          },
        ]}
      />
    </div>
  );
}
